//
// Copyright (c) 2026 Comcast
//

import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { OnChange } from 'react-final-form-listeners'

import TableFormBody from 'components/Shared/TableForm/TableFormBody'
import TableFormRow from 'components/Shared/TableForm/TableFormRow'
import TableFormGroupHeader from 'components/Shared/TableForm/TableFormGroupHeader'
import TableFormLabelCell from 'components/Shared/TableForm/TableFormLabelCell'
import TableFormInputCell from 'components/Shared/TableForm/TableFormInputCell'

import TextField from './Fields/TextField'
import TextAreaField from './Fields/TextAreaField'
import SwitchField from './Fields/SwitchField'
import ColorField from './Fields/ColorField'
import SelectField from './Fields/SelectField'
import SelectAsyncField from './Fields/SelectAsyncField'
import DateTimeField from './Fields/DateTimeField'
import ToggleButtonGroupField from './Fields/ToggleButtonGroupField'
import ConditionalField from './Fields/ConditionalField'
import ReadOnlyField from './Fields/ReadOnlyField'
import CreatableArrayField from './Fields/CreatableArrayField'
import SecureField from './Fields/SecureField'
import ImageField from './Fields/ImageField'
import IntegerField from './Fields/IntegerField'
import EditableArrayField from './Fields/EditableArrayField'
import RichTextField from './Fields/RichTextField'

import { getFieldGroups, getValidator } from './utils'

const StyledTableFormRow = styled(TableFormRow)`
  ${props => props.isReadOnlyView && css`
    opacity: 0.5;
  `}
`

const fieldComponents = {
  text: TextField,
  textarea: TextAreaField,
  switch: SwitchField,
  colorpicker: ColorField,
  select: SelectField,
  selectAsync: SelectAsyncField,
  datetime: DateTimeField,
  toggleButtonGroup: ToggleButtonGroupField,
  readOnlyField: ReadOnlyField,
  creatable_array: CreatableArrayField,
  secure: SecureField,
  image: ImageField,
  integer: IntegerField,
  editableArray: EditableArrayField,
  text_jira: RichTextField,
}

const TableFormFieldsBuilder = ({
  fieldParams, isInModal, form, activeTabId, flatFieldNames, userTimeZone = 'UTC',
}) => {
  const renderField = (field) => {
    const Component = fieldComponents[field.type] || TextField
    // remove key to get rid of React 19 warning
    // this component is wrapped with <ConditionalField> so key is not needed
    // key is set in Ticket Edit form. Consider renaming it here and there in case of some issues.
    const { key, ...restParams } = field
    return <Component {...restParams} />
  }

  const renderFields = (fieldGroup, fields) => {
    return fields.map(fieldOrig => {
      const field = { ...fieldOrig }

      field.validate = (value) => getValidator(value, field.required, field.validateFunc)
      field.isInModal = isInModal

      if (field.type === 'datetime' && !field.userTimeZone) {
        field.userTimeZone = userTimeZone
      }

      // do not prefix field names with group name and groupPrefix for old non-json forms,
      // otherwise fields will not match with initialValues
      if (!flatFieldNames) {
        const newFieldName =
          (fieldGroup.groupPrefix ? `${fieldGroup.groupPrefix}.` : '') +
          (fieldGroup.group ? `${fieldGroup.group}.` : '') +
          field.name
        field.name = newFieldName
      }

      const listKey = field.key || field.name
      const isReadOnlyView = Boolean(field.type === 'readOnlyField')
      const isCustomRowsRendering = Boolean(field.type === 'editableArray')

      // pass form object to onChange handler for fields which trigger form rebuilding on change
      // <OnChange> listener below is used for this purpose
      const onChange = (value, previous) => {
        field.onChange(value, previous, form, field)
      }

      return (
        <ConditionalField
          displayCondition={field.displayCondition}
          key={listKey}
        >
          {isCustomRowsRendering
            ? renderField(field)
            : (
              <StyledTableFormRow isReadOnlyView={isReadOnlyView}>
                <>
                  <TableFormLabelCell
                    required={field.required}
                    tooltip={field.tooltip}
                  >
                    {field.label}
                  </TableFormLabelCell>
                  <TableFormInputCell>
                    {renderField(field)}
                    {!!field.onChange && <OnChange name={field.name}>{onChange}</OnChange>}
                  </TableFormInputCell>
                </>
              </StyledTableFormRow>
              )
          }
        </ConditionalField>
      )
    })
  }

  const fieldGroups = getFieldGroups(fieldParams)
  let firstVisibleGroupIdx = -1

  return (
    <>
      {fieldGroups.map((fieldGroup, i) => {
        const fields = fieldGroup.fieldParams
        const hideTab = Boolean(activeTabId && fieldGroup.tabId !== activeTabId)
        if (!hideTab && (firstVisibleGroupIdx < 0)) {
          firstVisibleGroupIdx = i
        }

        return (
          <TableFormBody key={i} isHidden={hideTab}>
            <TableFormGroupHeader
              fieldGroup={fieldGroup}
              isFirst={i === firstVisibleGroupIdx}
              isInModal={isInModal}
            />
            {renderFields(fieldGroup, fields)}
          </TableFormBody>
        )
      })}
    </>
  )
}

TableFormFieldsBuilder.propTypes = {
  fieldParams: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        isDateOnly: PropTypes.bool,
        userTimeZone: PropTypes.string,
        label: PropTypes.string,
        tooltip: PropTypes.string,
        tooltipIcon: PropTypes.object,
        checkboxLabel: PropTypes.string,
        minRowsTextArea: PropTypes.number,
        minRows: PropTypes.number,
        multiselect: PropTypes.bool,
        maxLength: PropTypes.number,
        isReadOnly: PropTypes.bool,
        isMultiselect: PropTypes.bool,
        isLoading: PropTypes.bool,
        key: PropTypes.string,
        isHtml: PropTypes.bool,
        readOnlyFieldValue: PropTypes.string,
        indexInGroup: PropTypes.number,
      }),
      PropTypes.shape({
        group: PropTypes.string.isRequired,
        groupPrefix: PropTypes.string,
        groupTitle: PropTypes.string,
        tabId: PropTypes.string,
        fieldParams: PropTypes.array.isRequired,
      }),
    ]),
  ).isRequired,
  isInModal: PropTypes.bool,
  form: PropTypes.object,
  activeTabId: PropTypes.string,
  flatFieldNames: PropTypes.bool,
  userTimeZone: PropTypes.string,
}

export default TableFormFieldsBuilder
